import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import cognitoAuthService from '../services/cognitoAuthService';

/**
 * MFASetupForm component for setting up MFA during first login.
 * 
 * This is displayed when a user is required to set up MFA before they can complete login.
 */
const MFASetupForm = ({ username, session, onSetupComplete, onCancel }) => {
  const [step, setStep] = useState('loading'); // 'loading', 'scan', 'verify'
  const [secret, setSecret] = useState('');
  const [qrCodeUri, setQrCodeUri] = useState('');
  const [currentSession, setCurrentSession] = useState(session);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Ref to prevent double API calls in React Strict Mode
  const initCalledRef = useRef(false);

  // Initialize MFA setup
  useEffect(() => {
    const initMfaSetup = async () => {
      // Prevent double call in React Strict Mode
      if (initCalledRef.current) {
        return;
      }
      initCalledRef.current = true;
      
      try {
        setError('');
        const result = await cognitoAuthService.setupMfaWithSession(username, session);
        setSecret(result.secret);
        setQrCodeUri(result.qr_code_uri);
        setCurrentSession(result.session);
        setStep('scan');
      } catch (err) {
        setError(err.message || 'Failed to initialize MFA setup');
        setStep('error');
      }
    };

    initMfaSetup();
  }, [username, session]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await cognitoAuthService.verifyMfaSetupWithSession(
        username,
        currentSession,
        verificationCode
      );
      
      if (result.re_login_required) {
        // MFA was set up but user needs to re-login (pool-level MFA enforcement)
        setStep('re-login');
        return;
      }
      
      if (result.success) {
        onSetupComplete(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to verify MFA code');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate QR code URL using Google Charts API (or any QR service)
  const qrCodeImageUrl = qrCodeUri 
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUri)}`
    : '';

  if (step === 'loading') {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>Setting Up MFA</h2>
          <p className="text-muted">Please wait while we prepare your authenticator setup...</p>
          <div className="d-flex justify-content-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>MFA Setup Error</h2>
          <div className="alert alert-danger">{error}</div>
          <button 
            className="btn btn-secondary w-100" 
            onClick={onCancel}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (step === 're-login') {
    return (
      <div className="login-container">
        <div className="login-form">
          <h2>MFA Setup Complete ✓</h2>
          <div className="alert alert-success">
            Your authenticator app has been configured successfully. Please log in again using your username, password, and authenticator code.
          </div>
          <button 
            className="btn btn-primary w-100" 
            onClick={onCancel}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form" style={{ maxWidth: '450px' }}>
        <h2>Set Up Authenticator</h2>
        <p className="text-muted mb-3">
          MFA is required. Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
        </p>
        
        {error && <div className="alert alert-danger">{error}</div>}
        
        {/* QR Code */}
        <div className="text-center mb-3">
          {qrCodeImageUrl && (
            <img 
              src={qrCodeImageUrl} 
              alt="MFA QR Code" 
              style={{ maxWidth: '200px', border: '1px solid #ddd', borderRadius: '8px' }}
            />
          )}
        </div>
        
        {/* Manual entry option */}
        <div className="mb-3">
          <details>
            <summary className="text-muted" style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
              Can't scan? Enter code manually
            </summary>
            <div className="mt-2 p-2 bg-light rounded">
              <code style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>{secret}</code>
            </div>
          </details>
        </div>
        
        {/* Verification form */}
        <form onSubmit={handleVerify}>
          <div className="mb-3">
            <label htmlFor="verificationCode" className="form-label">
              Enter the 6-digit code from your authenticator app
            </label>
            <input
              type="text"
              className="form-control form-control-lg text-center"
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              pattern="\d{6}"
              required
              autoFocus
              style={{ letterSpacing: '0.5em', fontFamily: 'monospace' }}
            />
          </div>
          
          <div className="d-grid gap-2">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={isLoading || verificationCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify & Complete Setup'}
            </button>
            
            <button 
              type="button" 
              className="btn btn-outline-secondary"
              onClick={onCancel}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MFASetupForm;
